import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  evidenceAcceptanceSchema,
  evidenceKeepSchema,
} from '@/components/deals/evidence/evidence-review-schema'

const CURRENCY = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
})

function targetPath(target) {
  let path = `scheme.${target.scheme}`
  if (target.rowId || target.row_id) {
    path += `.${target.scheme === 'hmo' ? 'rooms' : 'units'}[${target.rowId || target.row_id}]`
  }
  return `${path}.${target.field}`
}

function labelFor(suggestion) {
  if (suggestion.title) return suggestion.title
  return suggestion.target?.field?.replace(/([a-z])([A-Z])/g, '$1 $2') || 'Market assumption'
}

function formatValue(value, unit) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'Unavailable'
  if (unit === 'GBP') return CURRENCY.format(number)
  if (unit === 'GBP/month') return `${CURRENCY.format(number)} / month`
  return `${value} ${unit || ''}`.trim()
}

function contextualMetadata(suggestion) {
  if (suggestion.evidenceQuality !== 'contextual') return null
  const sampleSize = Number(suggestion.sampleSize)
  const radius = suggestion.dependencies?.radiusMiles
  const scope = [
    Number.isInteger(sampleSize) && sampleSize > 0 ? `${sampleSize} comparables` : null,
    radius ? `within ${radius} miles` : null,
  ].filter(Boolean).join(' ')
  return scope ? `${scope}. Review before adopting.` : 'Review before adopting.'
}

function defaultValues(evidence) {
  return {
    reason: '',
    changes: (evidence?.suggestions ?? []).map((suggestion) => ({
      selected: false,
      suggestedValue: String(suggestion.value ?? ''),
      value: String(suggestion.value ?? ''),
    })),
  }
}

function requestBase(evidence, decision, reason) {
  return {
    idempotencyKey: `evidence-review-${crypto.randomUUID()}`,
    snapshotId: evidence.availableSnapshot.id,
    expectedContextRevision: evidence.context.revision,
    expectedCurrentInputsRevision: evidence.adoptedBasis.currentInputsRevision,
    decision,
    reviewedSuggestionIds: evidence.suggestions.map((suggestion) => suggestion.id),
    reason: reason.trim(),
  }
}

export function EvidenceReviewDialog({ open, onOpenChange, evidence, onSubmit, conflict = null }) {
  const [submissionError, setSubmissionError] = useState(null)
  const form = useForm({
    resolver: zodResolver(evidenceAcceptanceSchema),
    defaultValues: defaultValues(evidence),
  })

  const submitReview = async (request) => {
    try {
      await onSubmit(request)
      onOpenChange(false)
    } catch (error) {
      setSubmissionError(error.message)
    }
  }

  const acceptSelected = async (values) => {
    const changes = values.changes.flatMap((change, index) => {
      if (!change.selected) return []
      const suggestion = evidence.suggestions[index]
      const overridden = change.value !== change.suggestedValue
      return [{
        target: suggestion.target,
        suggestionId: suggestion.id,
        value: change.value,
        unit: suggestion.unit,
        ...(overridden ? { reason: values.reason.trim() } : {}),
      }]
    })
    await submitReview({ ...requestBase(evidence, 'accept', values.reason), changes })
  }

  const keepExisting = async () => {
    const reason = form.getValues('reason')
    const validation = evidenceKeepSchema.safeParse({ reason })
    if (!validation.success) {
      form.setError('reason', { message: validation.error.issues[0].message })
      return
    }
    await submitReview({ ...requestBase(evidence, 'keep', reason), changes: [] })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={form.handleSubmit(acceptSelected)} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Review market assumptions</DialogTitle>
            <DialogDescription>
              Compare current assumptions with saved market evidence. Nothing changes until you confirm.
            </DialogDescription>
          </DialogHeader>

          {conflict ? <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{conflict}</p> : null}
          {submissionError ? <p role="alert" className="text-sm text-destructive">{submissionError}</p> : null}

          <div className="space-y-3">
            {evidence.suggestions.map((suggestion, index) => {
              const path = targetPath(suggestion.target)
              const current = evidence.adoptedBasis.values[path]
              const contextual = contextualMetadata(suggestion)
              return (
                <section
                  key={suggestion.id}
                  data-testid={`suggestion-${suggestion.id}`}
                  className="grid gap-4 rounded-lg border border-border p-4 sm:grid-cols-[auto_1fr]"
                >
                  <Controller
                    control={form.control}
                    name={`changes.${index}.selected`}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                        aria-label={`Adopt ${labelFor(suggestion)}`}
                      />
                    )}
                  />
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-foreground">{labelFor(suggestion)}</h3>
                      {contextual ? (
                        <Badge variant="secondary">Limited market average</Badge>
                      ) : null}
                    </div>
                    {contextual ? (
                      <p className="text-xs text-muted-foreground">{contextual}</p>
                    ) : null}
                    <dl className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="text-muted-foreground">Current assumption</dt>
                        <dd className="mt-1 font-medium text-foreground">{formatValue(current, suggestion.unit)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Evidence suggestion</dt>
                        <dd className="mt-1 font-medium text-foreground">{formatValue(suggestion.value, suggestion.unit)}</dd>
                      </div>
                    </dl>
                    <Label htmlFor={`adopted-value-${index}`}>Adopted value</Label>
                    <Input
                      id={`adopted-value-${index}`}
                      aria-label={`Adopted value for ${labelFor(suggestion)}`}
                      {...form.register(`changes.${index}.value`)}
                    />
                  </div>
                </section>
              )
            })}
          </div>

          {form.formState.errors.root ? (
            <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="evidence-review-reason">Review reason</Label>
            <Textarea
              id="evidence-review-reason"
              aria-label="Review reason"
              placeholder="Required when changing a suggested value or keeping existing assumptions"
              {...form.register('reason')}
            />
            {form.formState.errors.reason ? (
              <p className="text-sm text-destructive">{form.formState.errors.reason.message}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="button" variant="outline" disabled={Boolean(conflict)} onClick={() => { void keepExisting() }}>
              Keep existing
            </Button>
            <Button type="submit" disabled={Boolean(conflict)}>Adopt selected</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
